/**
 * Director "view as" preview. Lets a Director walk the whole app the way a
 * teacher, admin, Jr assistant, or parent sees it, from their own login, so
 * the staff-facing and family-facing apps can be tested before real accounts
 * exist, and so a Director on the real Hub can see what a role's screens
 * look like without a second account (Bobbie, packet J3: "I would like to
 * see what the admin view see").
 *
 * How it stays safe:
 *   - This is a DISPLAY override only. Firestore rules and every Netlify
 *     function re-authorize against the caller's real Firebase identity, so a
 *     previewed (always-downgraded) role can never grant access the Director
 *     did not already have. See useUserDoc for where the override is applied,
 *     which additionally requires the REAL role to be an ACTIVE Director
 *     (see canPreviewRole below). It works on any host now, not only the
 *     demo/staging hosts; nothing here reads isDemoStudioEnabled.
 *   - 'director' is never a preview target, so preview can only reduce what
 *     the UI shows, never elevate it.
 *   - The only door that starts a preview is the "See it as" card at the
 *     bottom of Manage Users (and, on the demo hosts only, DemoView's
 *     /demo/:role links). Nothing on the home page, nothing in the
 *     hamburger.
 *   - UID-SCOPED (fixed 2026-09-10, adversarial review on PR #1089): the
 *     stored value is `{ uid, role }`, not a bare role string, and every
 *     read is checked against the CURRENT signed-in uid by
 *     resolveStoredPreviewRole. Without this, a Director who starts a
 *     preview and then signs out on a shared device left the bare role
 *     string sitting in localStorage (auth.signOut() never touched it), so
 *     the signed-out screen and the next account on that device inherited
 *     the previous Director's preview banner - and if the next account was
 *     also an active Director, useUserDoc downgraded their UI to that role
 *     immediately. auth.ts's signOut() now also calls setPreviewRole(null)
 *     directly as the second half of the fix, but the uid scope is the one
 *     that holds even if some future sign-out path forgets to call it.
 */
import { useSyncExternalStore } from 'react';
import type { UserRole, UserStatus } from './users';

const STORAGE_KEY = 'ldg-preview-role';
const EVENT = 'ldg-preview-role-change';

/** Roles a Director may preview. Director is intentionally excluded. */
export type PreviewRole = Exclude<UserRole, 'director'>;

export const PREVIEW_ROLES: PreviewRole[] = ['manager', 'company', 'ensemble', 'foh', 'family'];

/**
 * THE gate: may this account start or hold a role preview? An active
 * Director, full stop, on any host. Pulled out as a pure function so it is
 * one thing in one place (checked by useUserDoc on every render, and pinned
 * by tests without mounting React or Firebase) instead of the condition
 * being retyped wherever it is needed.
 */
export function canPreviewRole(
  realRole: UserRole | null | undefined,
  realStatus: UserStatus | null | undefined
): boolean {
  return realRole === 'director' && realStatus === 'active';
}

/** Plain-words label for the banner, matching Bobbie's role names. */
export const PREVIEW_ROLE_LABEL: Record<PreviewRole, string> = {
  manager: 'Manager',
  company: 'Teacher',
  ensemble: 'Jr Assistant',
  foh: 'Admin',
  family: 'Parent',
};

function isPreviewRole(value: unknown): value is PreviewRole {
  return typeof value === 'string' && (PREVIEW_ROLES as string[]).includes(value);
}

/** The shape written to localStorage: a role means nothing without the uid it belongs to. */
interface StoredPreview {
  uid: string;
  role: PreviewRole;
}

/**
 * THE decision, pulled out pure so a test can drive it directly with a
 * fabricated raw string and uid - no window, no localStorage, no Firebase.
 * A stored preview only ever resolves for the SAME signed-in account that
 * started it: a different (or absent) current uid reads as no preview,
 * which is what stops a signed-out screen, or the next account on a shared
 * device, from inheriting a previous Director's role preview.
 */
export function resolveStoredPreviewRole(
  raw: string | null | undefined,
  currentUid: string | null | undefined
): PreviewRole | null {
  if (!raw || !currentUid) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const stored = parsed as Partial<StoredPreview>;
  if (stored.uid !== currentUid) return null;
  return isPreviewRole(stored.role) ? stored.role : null;
}

/** Read the stored preview, scoped to `currentUid` (pass the signed-in uid, or null). */
export function getPreviewRole(currentUid: string | null | undefined): PreviewRole | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return resolveStoredPreviewRole(raw, currentUid);
  } catch {
    return null;
  }
}

/**
 * Start (or clear) a preview. Starting one requires the uid it belongs to -
 * without it the value could never be read back (resolveStoredPreviewRole
 * always requires a uid match), so a call that is missing one just clears
 * instead of writing a value nothing can ever use. Clearing (role=null)
 * never needs a uid: sign-out calls `setPreviewRole(null)` with none at all.
 */
export function setPreviewRole(role: PreviewRole | null, uid?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (role && uid) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ uid, role } satisfies StoredPreview));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore storage failures; preview just won't persist
  }
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVENT, callback);
  // Cross-tab: another tab writing localStorage fires a 'storage' event.
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

/**
 * Subscribe to the current preview role (null when not previewing).
 * `uid` is the CURRENT signed-in uid (pass null when signed out) - the
 * caller already has it from useAuth(), so it is threaded through rather
 * than read from a Firebase singleton here, keeping this module free of any
 * runtime Firebase import.
 */
export function usePreviewRole(uid: string | null | undefined): PreviewRole | null {
  const getSnapshot = () => getPreviewRole(uid);
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}
